import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy, useEffect } from 'react'

import { useAuth } from './lib/auth/useAuth'
import { keycloak } from './lib/auth/keycloak'
import { MainLayout } from './components/layout/MainLayout'
import { LoadingSpinner } from './components/ui/loading-spinner'
import { protectedRoutes } from './routes'

// Lazy load public pages (self-registration stays reachable unauthenticated).
const SignupPage = lazy(() =>
  import('./pages/SignupPage').then((m) => ({ default: m.SignupPage }))
)
// Public Trust Center — UNAUTHENTICATED, rendered outside ProtectedRoute/Keycloak.
const PublicTrustPage = lazy(() =>
  import('./pages/public/PublicTrustPage').then((m) => ({ default: m.PublicTrustPage }))
)
// Public Business Storefront ("Ma Page") — UNAUTHENTICATED published-page viewer.
const PublicBusinessPage = lazy(() =>
  import('./pages/public/PublicBusinessPage').then((m) => ({ default: m.PublicBusinessPage }))
)
// Public build/version page — UNAUTHENTICATED, reachable by opening /versionsbuilt.
const VersionsBuiltPage = lazy(() =>
  import('./pages/public/VersionsBuiltPage').then((m) => ({ default: m.VersionsBuiltPage }))
)

// Loading fallback
function PageLoading() {
  return (
    <div className="flex h-full items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
}

// There is NO in-app login/landing page. Unauthenticated visitors are sent
// straight to the Keycloak hosted login; authenticated visitors go to the app.
// (AuthProvider's onLoad:'login-required' handles the common case before this
// even renders — this component is the redirect target for '/' and legacy
// '/login' links and a safety net if init resolved unauthenticated.)
function LoginRedirect() {
  const { isAuthenticated, isLoading } = useAuth()

  useEffect(() => {
    if (isLoading) return
    if (!isAuthenticated) {
      keycloak.login({ redirectUri: window.location.origin + '/' })
    }
  }, [isAuthenticated, isLoading])

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }
  return <PageLoading />
}

// Protected route wrapper — sends unauthenticated users to the Keycloak hosted
// login (never an in-app page).
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      keycloak.login({ redirectUri: window.location.href })
    }
  }, [isLoading, isAuthenticated])

  if (isLoading || !isAuthenticated) {
    return <PageLoading />
  }

  return <>{children}</>
}

// Recursively render RouteObject[] as <Route> elements
function renderRoutes(routes: typeof protectedRoutes) {
  return routes.map((route, i) => {
    const key = route.path ?? `index-${i}`
    if (route.children) {
      return (
        <Route key={key} path={route.path} element={route.element} index={route.index as any}>
          {renderRoutes(route.children)}
        </Route>
      )
    }
    if (route.index) {
      return <Route key={key} index element={route.element} />
    }
    return <Route key={key} path={route.path} element={route.element} />
  })
}

export default function App() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Public routes (reachable WITHOUT a Keycloak session) */}
        <Route path="/signup" element={<SignupPage />} />
        {/* Public Trust Center — no login required, standalone layout (no sidebar) */}
        <Route path="/trust/:slug" element={<PublicTrustPage />} />
        {/* Public Business Storefront — no login required, self-contained branded page */}
        <Route path="/page/:slug" element={<PublicBusinessPage />} />
        {/* Public build/version page — no login required */}
        <Route path="/versionsbuilt" element={<VersionsBuiltPage />} />

        {/* Login/landing entrypoints now bounce straight to the Keycloak hosted
            login — there is no in-app login page. '/login' is kept only so legacy
            links resolve to the redirect. */}
        <Route path="/" element={<LoginRedirect />} />
        <Route path="/login" element={<LoginRedirect />} />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {renderRoutes(protectedRoutes)}
        </Route>
      </Routes>
    </Suspense>
  )
}
